import { Box } from '@mui/material';
import { useDeleteExerciseById } from '_api/mutations';
import { Exercise } from '_api/types';
import { ExerciseRouteMap, TherapyTypeValue } from '_constants';
import { ThumbnailView } from 'components/atoms';
import { ActionsFlexBox } from 'components/molecules';
import { ViewButtons } from 'components/molecules/types';
import { TableHeader } from 'components/organisms';
import { AddExerciseToPatientModal } from 'components/organisms/modals';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigateTo } from 'utils';
import { useExercise, useSearchResults, useUser } from 'utils/hooks';

export const ExerciseManagement = () => {
  const { existingSearchTerm, updateSearchTerm } = useSearchResults();
  const navigate = useNavigateTo();
  const toExerciseFormView = () => navigate(ExerciseRouteMap.ADDFORM);
  const { allExercise, isLoading } = useExercise();
  const { canEditObject } = useUser();
  const { mutateAsync: deleteExercise } = useDeleteExerciseById();

  const [id, setId] = useState(0);
  const [exercise, setExercise] = useState<Exercise | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState(existingSearchTerm || '');
  const [filteredData, setFilteredData] = useState<Exercise[]>([]);

  useEffect(() => setSearchTerm(existingSearchTerm || ''), [existingSearchTerm]);

  useEffect(() => {
    if (allExercise) {
      setFilteredData(allExercise);
    }
  }, [allExercise]);

  useEffect(() => {
    if (allExercise) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      const filtered = allExercise.filter((exercise) => {
        const title = exercise.title.toLowerCase();
        const therapyType = exercise.defaultTherapyType?.toString().toLowerCase() || '';
        const therapyValue = exercise.defaultTherapyType
          ? TherapyTypeValue[exercise.defaultTherapyType].toLowerCase()
          : '';
        const categories = exercise.category.map(
          (cate, i) =>
            `${cate.title.toLowerCase()}${i < exercise.category.length - 1 ? ', ' : ''}`,
        );

        return (
          title.includes(lowercasedSearchTerm) ||
          therapyType.includes(lowercasedSearchTerm) ||
          therapyValue.includes(lowercasedSearchTerm) ||
          categories.some((category) => category.includes(lowercasedSearchTerm))
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, allExercise]);

  const handleViewOnClick = (id: number) => {
    navigate(ExerciseRouteMap.DETAIL, id.toString());
  };

  const handleEditOnClick = (id: number) => {
    navigate(ExerciseRouteMap.EDIT, id.toString());
  };

  const handleDeleteOnClick = (id: number) => {
    deleteExercise({ id });
  };

  const handleAddOnClick = (id: number, exc: Exercise) => {
    setId(id);
    setExercise(exc);
  };

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    updateSearchTerm(searchTerm);
  };

  const actionButtons: ViewButtons = {
    viewButton: true,
    editButton: true,
    deleteButton: true,
    addButton: true,
  };

  const tableColumns = useMemo<MRT_ColumnDef<Exercise>[]>(
    () => [
      {
        header: 'Exercise',
        enableSorting: false,
        Cell: ({ row }) => (
          <ThumbnailView fullImageUrl={row.original.fullImageUrl} rowView={true} />
        ),
      },
      {
        header: 'Title',
        accessorFn: (row) => row.title,
      },
      {
        header: 'Type',
        accessorFn: (row) =>
          row.defaultTherapyType ? TherapyTypeValue[row.defaultTherapyType] : '',
      },
      {
        header: 'Category',
        accessorFn: (row) =>
          row.category.map(
            (cate, i) => `${cate.title}${i < row.category.length - 1 ? ', ' : ''}`,
          ),
      },
      {
        header: 'Actions',
        enableSorting: false,
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={'exercise'}
            viewOnClick={() => handleViewOnClick(row.id)}
            editOnClick={() => handleEditOnClick(row.id)}
            deleteOnClick={() => handleDeleteOnClick(row.id)}
            addOnClick={() => handleAddOnClick(row.id, row)}
            actionButtons={actionButtons}
            disableDeleteTooltip={
              canEditObject(row)
                ? undefined
                : 'Cannot delete exercises you did not create'
            }
          />
        ),
        size: 10,
      },
    ],
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TableHeader
          onAddClick={toExerciseFormView}
          handleSearch={handleSearch}
          value={searchTerm}
        />
      </Box>
      <Box sx={{ marginTop: 10 }}>
        {filteredData && !isLoading ? (
          <MaterialReactTable
            columns={tableColumns}
            data={filteredData}
            enableSorting={true}
            enableColumnActions={false}
            enableTopToolbar={false}
            enablePagination={true}
          />
        ) : null}
      </Box>
      <AddExerciseToPatientModal
        content={exercise as Exercise}
        open={!!exercise}
        onClose={() => setExercise(undefined)}
        id={id}
      />
    </Box>
  );
};
